'use client';

import { useCallback, useMemo, useState } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { UserRejectedRequestError, BaseError } from 'viem';
import { useAccount, useReadContract, useWalletClient } from 'wagmi';

import { erc20Abi } from '@/abi';

import type { Address } from 'viem';

interface I_UseTokenImportParams {
  tokenAddress?: Address;
  tokenSymbol: string;
  fallbackDecimals?: number;
}

interface I_UseTokenImportReturn {
  actualDecimals: number | undefined;
  shouldImportToken: boolean;
  isImporting: boolean;
  showImportCheckbox: boolean;
  setShouldImportToken: (value: boolean) => void;
  importTokenToWallet: () => Promise<boolean>;
}

// Proper user rejection detection using viem error types
const isUserRejection = (error: unknown): boolean => {
  if (error instanceof UserRejectedRequestError) return true;

  if (error instanceof BaseError) {
    const cause = error.cause;
    if (cause && typeof cause === 'object') {
      const causeStr = String(cause).toLowerCase();
      return (
        causeStr.includes('user rejected') ||
        causeStr.includes('user denied') ||
        causeStr.includes('rejected') ||
        causeStr.includes('denied') ||
        causeStr.includes('cancelled') ||
        causeStr.includes('canceled')
      );
    }
  }
  return false;
};

export function useTokenImport({
  tokenAddress,
  tokenSymbol,
  fallbackDecimals
}: I_UseTokenImportParams): I_UseTokenImportReturn {
  const t = useTranslations('pools');
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();

  // Read token decimals directly from contract (more reliable than pool data)
  const { data: tokenDecimals } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'decimals',
    query: { enabled: !!tokenAddress }
  }) as { data: number | undefined };

  // Use contract decimals if available, fallback to pool data decimals
  const actualDecimals = tokenDecimals ?? fallbackDecimals;

  // Checkbox state for importing token
  const [shouldImportToken, setShouldImportToken] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Show checkbox only if:
  // 1. Token address exists
  // 2. Decimals are loaded (or we have fallback)
  const showImportCheckbox = useMemo(() => {
    return (
      !!tokenAddress &&
      (actualDecimals !== undefined || fallbackDecimals !== undefined)
    );
  }, [tokenAddress, actualDecimals, fallbackDecimals]);

  const importTokenToWallet = useCallback(async (): Promise<boolean> => {
    if (!tokenAddress || !walletClient || !address) {
      return false;
    }

    // Ensure we have decimals before importing
    if (actualDecimals === undefined || actualDecimals === null) {
      toast.error(t('tokenImportFailed'));
      console.error('Token decimals not available');
      return false;
    }

    try {
      setIsImporting(true);
      await walletClient.watchAsset({
        type: 'ERC20',
        options: {
          address: tokenAddress,
          symbol: tokenSymbol,
          decimals: actualDecimals
        }
      });

      toast.success(t('tokenImportedSuccessfully'));
      return true;
    } catch (error) {
      const userRejected = isUserRejection(error);
      if (userRejected) {
        toast.error(t('requestRejected'));
      } else {
        const displayError =
          error instanceof Error ? error.message : t('tokenImportFailed');
        toast.error(displayError);
      }
      console.error('Token import error:', error);
      return false;
    } finally {
      setIsImporting(false);
    }
  }, [tokenAddress, walletClient, address, tokenSymbol, actualDecimals, t]);

  return {
    actualDecimals,
    shouldImportToken,
    isImporting,
    showImportCheckbox,
    setShouldImportToken,
    importTokenToWallet
  };
}
